/**
 * Client for the platform-admin agent-bus ops surface
 * (`GET/POST /api/platform/runtimes/:userId/bus/*`).
 *
 * Data path (agent-bus plan §7 P5 Step 13):
 *   LexPortal → lexserver `/api/platform/runtimes/:userId/bus/*`
 *   (pure passthrough proxy, `lexserver/src/routes/platform.ts`) →
 *   runtime `/bus/*` (lexiun-core's own gateway mount, NOT `/api/lx/*`) →
 *   bus-service :3230 (the broker).
 *
 * Two response shapes can come back and BOTH must be tolerated (mirrors
 * `tasklist-api.ts`'s `tasklistGet`): lexserver's own errors (e.g.
 * `RUNTIME_NOT_FOUND` when no runtime is provisioned) are enveloped as
 * `{ok:false,error:{code,message}}`, while bus-service's own errors — which
 * this proxy forwards VERBATIM, unwrapped — are `{error:"bare_code",
 * message,detail}` (see `bus-service/src/router.ts`'s error handler). A
 * successful body from bus-service is also unwrapped (no `{ok,data}`).
 *
 * Full REST contract + field meanings: `ai-docs/bus-service-guide.md` §4
 * (routes), §6 (lifecycle), §12 (runbook — the stuck-alias / unattended-lane
 * scenarios this panel exists to surface).
 */

import { getLexiunToken } from "./lexiun-jwt";
import { newTraceparent } from "./traceparent";

const LEXSERVER_BASE = "/lexserver";

/** Per-alias ops snapshot — `GET /bus/status`. */
export interface BusAliasStatus {
  alias: string;
  kind: "concierge" | "specialist" | "mission" | "harness" | string;
  durable: boolean;
  queued: number;
  processing: number;
  lastPollAt?: string;
  /** Queued messages nobody has polled within the staleness window — the
   *  "unattended lane" runbook scenario (guide §12). */
  unattended: boolean;
}

export interface BusStatusResult {
  aliases: BusAliasStatus[];
}

/** The alias directory — `GET /bus/agents`. Adds the human-facing
 *  description the status route doesn't carry. */
export interface BusAgentEntry {
  alias: string;
  description: string;
  kind: "concierge" | "specialist" | "mission" | "harness" | string;
  durable: boolean;
  registeredAt: string;
  lastPollAt?: string;
}

export interface BusAgentsResult {
  agents: BusAgentEntry[];
}

export type BusMessageKind = "request" | "notify" | "reply" | "info";
export type BusMessageStatus = "queued" | "claimed" | "done" | "failed" | "expired" | "canceled";

/** A resolved (terminal) envelope from the 7-day history window —
 *  `GET /bus/history`. Parts are omitted here: the ops panel is a
 *  diagnostic surface, not a message viewer (mirrors `lx_bus_history`'s
 *  metadata-only posture, guide §8). */
export interface BusHistoryEntry {
  id: string;
  kind: BusMessageKind;
  from: string;
  to: string;
  thread?: string;
  status: BusMessageStatus;
  created: string;
  claimedAt?: string;
  claimedBy?: string;
  resolvedAt?: string;
  summary?: string;
}

export interface BusHistoryResult {
  messages: BusHistoryEntry[];
}

/**
 * The EVENT journal — `GET /bus/journal`. Distinct from history on purpose:
 * `history` returns terminal MESSAGES, so it is structurally incapable of
 * showing an event that never became one. `REFUSE` (an undeliverable send),
 * `REAP` (a stale registration) and `EXPIRE` exist only here — and a REFUSE
 * row is the ONLY trace that a lost message was ever attempted.
 *
 * The 10 members below are the COMPLETE vocabulary, mirrored from
 * `bus-service/src/bus-store.ts`'s `JournalEvent`. There is deliberately no
 * `ACK` (it is a `HANDBACK` with `note:"acked"`) and no `DELIVER` (nothing
 * ever emitted one) — both were removed upstream on 2026-08-18 rather than
 * left as filter options that return nothing forever while an operator reads
 * "no acks happened". A test pins the count so a re-added vapor member fails
 * here rather than in front of an operator.
 */
export const JOURNAL_EVENTS = [
  "REGISTER",
  "UNREGISTER",
  "SEND",
  "CLAIM",
  "HANDBACK",
  "REQUEUE",
  "EXPIRE",
  "CANCEL",
  "REAP",
  "REFUSE",
] as const;

export type BusJournalEvent = (typeof JOURNAL_EVENTS)[number];

/** Metadata only — journal rows never carry message `parts`, which is
 *  test-pinned upstream so this stays a safe ops surface. */
export interface BusJournalRow {
  ts: string;
  event: BusJournalEvent;
  id?: string;
  from?: string;
  to?: string;
  kind?: string;
  thread?: string;
  scope?: Record<string, string | undefined>;
  status?: string;
  note?: string;
}

export interface BusJournalResult {
  /** The partition ACTUALLY read, echoed by the service. Render THIS, never
   *  the day you asked for — they differ whenever `day` was omitted, and the
   *  service's clock is not the browser's. */
  day: string;
  /** True when the `limit` cap bit. A capped list shown as complete tells an
   *  operator "these are the refusals" when it means "these are the first N". */
  truncated: boolean;
  /** NEWEST FIRST, already — see `getBusJournal`. */
  rows: BusJournalRow[];
}

/** Error codes seen from either lexserver's own envelope or bus-service's
 *  forwarded body. Widened to `string` so an unknown code degrades to a
 *  generic message instead of a type error. */
export type BusApiErrorCode =
  | "RUNTIME_NOT_FOUND"
  | "unknown_alias"
  | "not_found"
  | "invalid_message"
  | "not_claimed"
  | "not_claimant"
  | "already_terminal"
  | "scope_denied"
  | "envelope_too_large"
  | "MISSING_BEARER_TOKEN"
  | "INVALID_TOKEN"
  | "PLATFORM_ADMIN_REQUIRED"
  | string;

export class BusApiError extends Error {
  constructor(
    public code: BusApiErrorCode,
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "BusApiError";
  }
}

/** Operator-facing sentence for a bus-ops failure. */
export function describeBusError(e: BusApiError): string {
  switch (e.code) {
    case "RUNTIME_NOT_FOUND":
      return "The selected runtime is not reachable.";
    case "unknown_alias":
    case "not_found":
      return "That message id was not found on this runtime's bus (already resolved, expired, or never existed).";
    case "not_claimed":
      return "That message is not currently claimed — there is nothing to requeue.";
    case "PLATFORM_ADMIN_REQUIRED":
      return "Platform-admin scope is required to read the agent bus.";
    default:
      return e.message || e.code;
  }
}

async function busRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getLexiunToken();
  const headers: Record<string, string> = {
    traceparent: newTraceparent(),
    ...(init?.body ? { "content-type": "application/json" } : {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(`${LEXSERVER_BASE}${path}`, { ...init, headers });
  const body = (await res.json().catch(() => ({}))) as {
    error?: { code?: string; message?: string } | string;
    message?: string;
  };
  if (!res.ok) {
    const err = typeof body.error === "object" ? body.error : undefined;
    throw new BusApiError(
      err?.code ?? (typeof body.error === "string" ? body.error : "BUS_ERROR"),
      err?.message ?? body.message ?? res.statusText,
      res.status,
    );
  }
  return body as T;
}

function base(userId: string): string {
  return `/api/platform/runtimes/${encodeURIComponent(userId)}/bus`;
}

/** Per-alias queue depths + unattended flag — the panel's primary ops read. */
export function getBusStatus(userId: string): Promise<BusStatusResult> {
  return busRequest<BusStatusResult>(`${base(userId)}/status`);
}

/** The alias directory (description + kind + durability). */
export function getBusAgents(userId: string): Promise<BusAgentsResult> {
  return busRequest<BusAgentsResult>(`${base(userId)}/agents`);
}

export interface BusHistoryFilters {
  alias?: string;
  thread?: string;
  since?: string;
}

/** Serialize history filters onto the query string. Exported for unit test —
 *  mirrors `buildTasklistQuery`'s reasoning: unlike that route's list params,
 *  every field here is a scalar, but an empty string must still be omitted
 *  rather than sent as `alias=`. */
export function buildBusHistoryQuery(filters: BusHistoryFilters): string {
  const qs = new URLSearchParams();
  if (filters.alias) qs.set("alias", filters.alias);
  if (filters.thread) qs.set("thread", filters.thread);
  if (filters.since) qs.set("since", filters.since);
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/**
 * The 7-day resolved-message window. All filters optional.
 *
 * ⚠ The broker itself returns OLDEST first (`bus-store.ts`'s `listHistory`
 * walks day-partitioned dirs in ascending sort order) — this client
 * re-sorts newest-first, which is what every dense-row UI actually wants,
 * so callers never have to remember the broker's on-disk walk order.
 */
export async function getBusHistory(
  userId: string,
  filters: BusHistoryFilters = {},
): Promise<BusHistoryResult> {
  const result = await busRequest<BusHistoryResult>(
    `${base(userId)}/history${buildBusHistoryQuery(filters)}`,
  );
  return {
    messages: [...result.messages].sort((a, b) => Date.parse(b.created) - Date.parse(a.created)),
  };
}

export interface BusJournalFilters {
  /** `YYYY-MM-DD`. OMIT to let the service pick its own today — see
   *  `journalDayUTC` for why a browser-computed day is a trap. */
  day?: string;
  event?: BusJournalEvent;
  /** Matches EITHER end (from OR to) — an operator chasing a lane does not
   *  know in advance which end it was, and on a REFUSE row that is precisely
   *  the unknown. */
  alias?: string;
  thread?: string;
  limit?: number;
}

/** Serialize journal filters. Exported for unit test, mirroring
 *  `buildBusHistoryQuery`: an empty string must be OMITTED, never sent as
 *  `event=`, which the service would treat as a filter matching nothing. */
export function buildBusJournalQuery(filters: BusJournalFilters): string {
  const qs = new URLSearchParams();
  if (filters.day) qs.set("day", filters.day);
  if (filters.event) qs.set("event", filters.event);
  if (filters.alias) qs.set("alias", filters.alias);
  if (filters.thread) qs.set("thread", filters.thread);
  if (filters.limit !== undefined) qs.set("limit", String(filters.limit));
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/**
 * The day key for an instant in **UTC**.
 *
 * ⚠ NOT `toLocaleDateString()`. The service partitions the journal on ITS
 * clock, and this host runs UTC-3 — so a browser computing "today" locally
 * asks for the wrong partition for three hours either side of midnight and
 * gets an empty-but-successful 200, which is the exact "quiet bus" disguise
 * the service's own 400-on-malformed-day exists to prevent.
 */
export function journalDayUTC(at: Date): string {
  return at.toISOString().slice(0, 10);
}

/**
 * The event journal for one day.
 *
 * ⚠ **No client-side re-sort, deliberately.** Its sibling `getBusHistory`
 * re-sorts because the broker walks history's day-partitioned dirs oldest
 * first; the journal already returns NEWEST first (`bus-broker.ts`'s
 * `readJournal` reverses before capping, so the cap bites the old end).
 * Copying the sibling's sort here would silently reverse the rows AND move
 * the meaning of `truncated`. A test pins the pass-through.
 */
export function getBusJournal(
  userId: string,
  filters: BusJournalFilters = {},
): Promise<BusJournalResult> {
  return busRequest<BusJournalResult>(`${base(userId)}/journal${buildBusJournalQuery(filters)}`);
}

/**
 * Force a claim to release NOW instead of waiting out the 2h claim TTL — the
 * documented recovery action for a "stuck alias" (guide §12). This is the
 * one MUTATING call on the panel; it exists because the runbook names it as
 * the operator's recovery lever, not as a general bus-write surface.
 *
 * Returns the message back in `queued` status (the broker's `requeue()`
 * returns the full envelope, not a bare `{ok}` — see `bus-broker.ts`).
 */
export function requeueBusMessage(userId: string, id: string): Promise<BusHistoryEntry> {
  return busRequest<BusHistoryEntry>(`${base(userId)}/requeue`, {
    method: "POST",
    body: JSON.stringify({ id }),
  });
}
